/** Helpers UI — Base de vencimientos (Calendario despacho). */

export const DUE_RULE_AUTHORITY_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "SAT", label: "SAT" },
  { key: "IMSS", label: "IMSS" },
  { key: "INFONAVIT", label: "INFONAVIT" },
  { key: "REPSE", label: "REPSE" },
  { key: "Estatal", label: "Estatal" },
  { key: "Municipal", label: "Municipal" },
  { key: "Interna", label: "Interna" },
];

/** Filtros para calendario fiscal (sin obligaciones internas). */
export const FISCAL_AUTHORITY_FILTERS = DUE_RULE_AUTHORITY_FILTERS.filter(
  (f) => f.key !== "Interna",
);

/** Filtros calendario operativo (solo obligaciones internas). */
export const OPERATIONAL_AUTHORITY_FILTERS = [
  { key: "Interna", label: "Interna" },
];

export const RECURRENCE_PERIODICITY_OPTIONS = [
  { value: "monthly", label: "Repetitivo mensual" },
  { value: "annual", label: "Repetitivo anual" },
  { value: "bimestral", label: "Repetitivo bimestral" },
];

const PERIODICITY_LABELS = {
  monthly: "Repetitivo mensual",
  mensual: "Repetitivo mensual",
  annual: "Repetitivo anual",
  anual: "Repetitivo anual",
  bimestral: "Repetitivo bimestral",
  quarterly: "Trimestral",
  cuatrimestral: "Cuatrimestral",
  eventual: "Por evento",
  operational: "Operativa",
};

export function normalizeRulePeriodicity(value) {
  const raw = String(value || "monthly").trim().toLowerCase();
  const map = {
    mensual: "monthly",
    anual: "annual",
    cuatrimestral: "quarterly",
  };
  return map[raw] || raw || "monthly";
}

export function periodicityDisplayLabel(value) {
  const key = normalizeRulePeriodicity(value);
  return PERIODICITY_LABELS[key] || key;
}

export function recurrenceOptionsForPeriodicity(currentPeriodicity) {
  const normalized = normalizeRulePeriodicity(currentPeriodicity);
  const base = [...RECURRENCE_PERIODICITY_OPTIONS];
  if (normalized && !base.some((o) => o.value === normalized)) {
    base.push({ value: normalized, label: periodicityDisplayLabel(normalized) });
  }
  return base;
}

export function calculationTypeForPeriodicity(periodicity) {
  const p = normalizeRulePeriodicity(periodicity);
  if (p === "annual") return "fixed_annual_date";
  if (p === "bimestral") return "bimestral_next_month";
  return "editable_manual";
}

export const MESSAGE_RECURRENCE_OPTIONS = [
  { value: "monthly", label: "Repetitivo mensual" },
  { value: "annual", label: "Repetitivo anual" },
];

const MONTH_NAMES_SHORT = ["", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export function formatMessageRecurrenceLabel(row) {
  const mode = row?.recurrence_mode || "monthly";
  const day = Number(row?.recurrence_day_of_month) || 5;
  if (mode === "annual") {
    const month = Number(row?.recurrence_month) || 1;
    const monthLabel = MONTH_NAMES_SHORT[month] || month;
    return `Anual · ${day} ${monthLabel}`;
  }
  if (!row?.recurrence_mode && row?.schedule_based_on) {
    return `Mensual · día ${day} (migrado)`;
  }
  return `Mensual · día ${day} de cada mes`;
}

export function formatSampleDateLabel(value) {
  const raw = String(value || "").slice(0, 10);
  if (!raw || raw.length < 10) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
}

export const CALCULATION_TYPE_OPTIONS = [
  { value: "fixed_day_next_month", label: "Día fijo del mes siguiente" },
  { value: "last_day_next_month", label: "Último día del mes siguiente" },
  { value: "fixed_annual_date", label: "Fecha anual fija" },
  { value: "bimestral_next_month", label: "Bimestral — mes siguiente al bimestre" },
  { value: "operational_no_fixed_due", label: "Sin vencimiento fiscal fijo" },
  { value: "eventual", label: "Según evento" },
  { value: "editable_manual", label: "Regla editable" },
];

export const RULE_DEACTIVATION_REASONS = [
  { value: "cambio_fiscal", label: "Cambio fiscal" },
  { value: "ya_no_aplica", label: "Ya no aplica" },
  { value: "regla_duplicada", label: "Regla duplicada" },
  { value: "reemplazada", label: "Se reemplazó por otra" },
  { value: "error_configuracion", label: "Error de configuración" },
  { value: "otro", label: "Otro" },
];

export function dueRuleScopeLabel(row) {
  if (row?.has_workspace_override) return "Personalizada";
  if (row?.is_fallback) return "Fallback global";
  if (row?.rule_scope === "global") return "Global";
  return "—";
}

export function dueRuleStatusLabel(row) {
  if (!row?.active) return "Inactiva";
  if (!row?.calendar_enabled) return "Sin calendario";
  return "Activa";
}

const GENERIC_RULE_LABELS = /^fecha operativa editable$/i;

/** Resumen visible de la programación (día, offsets) para tablas y confirmaciones. */
export function formatRuleScheduleSummary(row) {
  const day = Number(row?.day_of_month);
  const op = row?.operational_offset_days;
  const rem = row?.reminder_offset_days;
  const parts = [];
  if (Number.isFinite(day) && day >= 1) parts.push(`día ${day}`);
  if (op != null && op !== "") parts.push(`op. ${op}d`);
  if (rem != null && rem !== "") parts.push(`rec. ${rem}d`);
  const schedule = parts.join(" · ");
  const label = String(row?.rule_label || "").trim();
  if (label && !GENERIC_RULE_LABELS.test(label)) {
    return schedule ? `${label} (${schedule})` : label;
  }
  return schedule || label || "—";
}

/** Etiqueta a persistir al guardar si el usuario dejó el texto genérico del seed. */
export function buildPersistedRuleLabel({ rule_label, day_of_month, operational_offset_days, reminder_offset_days }) {
  const custom = String(rule_label || "").trim();
  const day = Number(day_of_month);
  const op = Number(operational_offset_days);
  const rem = Number(reminder_offset_days);
  const schedule = [
    Number.isFinite(day) && day >= 1 ? `Día ${day}` : null,
    Number.isFinite(op) ? `op. ${op}d` : null,
    Number.isFinite(rem) ? `rec. ${rem}d` : null,
  ].filter(Boolean).join(" · ");
  if (custom && !GENERIC_RULE_LABELS.test(custom)) return custom;
  return schedule || custom || "Regla editable";
}
